import React, {useState, useEffect} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {datadogRum} from '@datadog/browser-rum'

import ajaxRequest from '../../assets/functions/ajaxRequest.js'
import hexColorIsLight from '../../assets/functions/hexColorIsLight.js'

import useAgeGate from '../../hooks/useAgeGate.jsx'
import useGoogleAnalytics from '../../hooks/useGoogleAnalytics'
import usePrerenderDetection from '../../hooks/usePrerenderDetection'

const Context = React.createContext()

export function Provider({children}) {
  const [authenticated, setAuthenticated] = useState(false)
  const [authenticityToken, setAuthenticityToken] = useState(
    document.querySelector('head meta[name="csrf-token"]').content,
  )

  const [brand, setBrand] = useState(false)
  const [offers, setOffers] = useState([])
  const [loadingOffers, setLoadingOffers] = useState(true)
  const [initialOffersLoaded, setInitialOffersLoaded] = useState(false)
  const [offersFailed, setOffersFailed] = useState(false)

  const [faqs, setFaqs] = useState([])
  const [selectedState, setSelectedState] = useState(false)
  const [initialPathname] = useState(allowedInitialPathname())

  const [consumer, setConsumer] = useState(false)
  const [loadingConsumer, setLoadingConsumer] = useState(false)

  const [consumerLimitReachedByOffer, setConsumerLimitReachedByOffer] =
    useState({})
  const [buttonTextColor, setButtonTextColor] = useState('text-dark')

  const [errorPopup, setErrorPopup] = useState({
    show: false,
    title: '',
    message: '',
    errors: [],
  })

  const history = useHistory()
  const location = useLocation()

  function showErrorPopup(title, message, errors = []) {
    setErrorPopup({title, message, errors, show: true})
  }

  function closeErrorPopup() {
    setErrorPopup((errorPopup) => ({...errorPopup, show: false}))
  }
  // Query string and hash from the initial URL
  const [initialSearch] = useState(location.search)
  const [initialHash] = useState(location.hash)

  function redirectURLBuilder(pathname) {
    return {pathname, search: initialSearch, hash: initialHash}
  }

  function redirectAfterAgeGate() {
    if (selectedState) history.push(initialPathname)
    else history.push(redirectURLBuilder('/location'))
  }

  const {age, setAge, handleAtLeast21, handleNot21} = useAgeGate(
    brand && brand.use_age_gate,
    brand,
    redirectAfterAgeGate,
  )

  const {browserIsPrerender} = usePrerenderDetection()

  // TODO move this property ID somewhere else
  // (need to differentiate between prod/staging/dev)
  const {gaEvent} = useGoogleAnalytics('UA-128051072-4')

  const rootProps = document.getElementById('root').dataset

  // Handles initial redirects for age gate and location, and sets selected state from localStorage if it exists
  useEffect(() => {
    // If browser is prerender, skip age gate and location and go to initialPathname
    if (browserIsPrerender) return history.push(initialPathname)
    // Fetch selected state from localStorage and set it
    const selectedStateFromStorage = JSON.parse(
      localStorage.getItem('selectedState'),
    )
    if (selectedStateFromStorage && !selectedState)
      setSelectedState(selectedStateFromStorage)

    if (!brand) return

    // If age gate is enabled and no age value, try to fetch age from localStorage and set it, otherwise go to age gate
    if (brand.use_age_gate) {
      // Fetch age from localStorage and set it
      const ageFromStorage = localStorage.getItem('age')
      if (ageFromStorage) setAge(ageFromStorage)

      // no age value, go to age gate
      if (!(ageFromStorage === 'atLeast21'))
        return history.push(redirectURLBuilder('/age'))
    } else {
      // If age gate is not enabled, set age to atLeast21
      setAge('atLeast21')
    }

    // If no selected state, go to location page
    if (selectedStateFromStorage) {
      history.push(initialPathname)
    } else {
      history.push(redirectURLBuilder('/location'))
    }
  }, [brand])

  useEffect(() => {
    setOffers([])
    setLoadingOffers(true)
    if (selectedState) {
      localStorage.setItem('selectedState', JSON.stringify(selectedState))
      ajaxRequest(
        'GET',
        `/offers?state_abbreviation=${selectedState.abbreviation}`,
        {
          headers: [{'X-CSRF-Token': authenticityToken}],
          json: true,
        },
      )
        .then((response) => {
          setOffers(response.body)
          setLoadingOffers(false)
          setInitialOffersLoaded(true)
          setAuthenticityToken(
            response.headers['xcsrf-token'] ||
              response.headers['XCSRF-Token'] ||
              authenticityToken,
          )
        })
        .catch((response) => {
          setLoadingOffers(false)
          setInitialOffersLoaded(true)
          setOffersFailed(true)
        })
    } else {
      ajaxRequest('GET', `/offers`, {
        headers: [{'X-CSRF-Token': authenticityToken}],
        json: true,
      })
        .then((response) => {
          setOffers(response.body)
          setLoadingOffers(false)
          setInitialOffersLoaded(true)
          setAuthenticityToken(
            response.headers['xcsrf-token'] ||
              response.headers['XCSRF-Token'] ||
              authenticityToken,
          )
        })
        .catch((response) => {
          setLoadingOffers(false)
          setInitialOffersLoaded(true)
          setOffersFailed(true)
        })
    }
  }, [selectedState, authenticated])

  useEffect(() => {
    getBrand()
    getConsumer()
  }, [])

  useEffect(() => {
    if (consumer) {
      const consumerLimitReachedByOffer = offers.reduce(
        (consumerLimitReachedByOffer, offer) => {
          const timesRedeemed = [
            consumer.pending_consumer_balance,
            ...consumer.past_consumer_balances,
          ]
            .filter(Boolean)
            .reduce((timesRedeemed, consumerBalance) => {
              return (timesRedeemed += consumerBalance.purchases.reduce(
                (timesRedeemedInPurchase, purchase) => {
                  return (timesRedeemedInPurchase +=
                    purchase.redemptions.filter((redemption) => {
                      return (
                        [
                          'accepted',
                          'disbursed',
                          'failed_payment_method',
                        ].includes(redemption.status) &&
                        redemption.offer_id === offer.id
                      )
                    }).length)
                },
                0,
              ))
            }, 0)

          consumerLimitReachedByOffer[offer.id] =
            timesRedeemed >= offer.per_consumer_limit
          return consumerLimitReachedByOffer
        },
        {},
      )

      setConsumerLimitReachedByOffer(consumerLimitReachedByOffer)
    }
  }, [consumer, offers])

  useEffect(() => {
    if (brand)
      setButtonTextColor(
        hexColorIsLight(brand.primary) ? 'text-dark' : 'text-light',
      )
  }, [brand])

  function getBrand() {
    ajaxRequest('GET', '/brand', {
      headers: [{'X-CSRF-Token': authenticityToken}],
      json: true,
    })
      .then((response) => {
        setBrand(response.body.brand)
        if (response.body.brand?.id) {
          datadogRum.setGlobalContextProperty(
            'brand_id',
            response.body.brand.id,
          )
        }
      })
      .catch((response) => {
        console.error('failed to get brand')
      })
  }

  function getConsumer() {
    setLoadingConsumer(true)
    ajaxRequest('GET', '/consumer', {
      headers: [{'X-CSRF-Token': authenticityToken}],
      json: true,
    })
      .then((response) => {
        setAuthenticated(true)
        setConsumer(response.body.consumer)
        setLoadingConsumer(false)
      })
      .catch((response) => {
        console.log('not authenticated')
        setLoadingConsumer(false)
      })
  }

  useEffect(() => {
    if (consumer?.id) {
      datadogRum.setUser({id: consumer.id})
    }
  }, [consumer])

  function logout() {
    ajaxRequest('GET', '/logout', {
      headers: [{'X-CSRF-Token': authenticityToken}],
      json: true,
    })
      .then((response) => {
        setAuthenticated(false)
        setConsumer(false)
        datadogRum.clearUser()
        history.push('/')
      })
      .catch((response) => console.error('Something went wrong'))
  }

  function allowedInitialPathname() {
    const {pathname, search, hash} = useLocation()

    const allowInitialPathname =
      ['/offer', '/balance', '/account', '/faqs'].filter(
        (allowedPartialInitialPathname) => {
          return pathname.includes(allowedPartialInitialPathname)
        },
      ).length > 0

    // Keep the query string and hash
    if (allowInitialPathname) return {pathname, search, hash}
    else return {pathname: '/', search, hash}
  }

  const value = {
    authenticated,
    authenticityToken,
    brand,
    offers,
    loadingOffers,
    initialOffersLoaded,
    faqs,
    selectedState,
    setSelectedState,
    browserIsPrerender,
    consumer,
    setConsumer,
    loadingConsumer,
    setLoadingConsumer,
    consumerLimitReachedByOffer,
    location,
    history,
    age,
    handleAtLeast21,
    handleNot21,
    gaEvent,
    getConsumer,
    logout,
    initialPathname,
    buttonTextColor,
    errorPopup,
    showErrorPopup,
    closeErrorPopup,
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export default Context
